/**
 * Identity Verification (IDV) Service
 * Handles identity verification initiation and confirmation
 */

import { CLAIMS_LEVEL, type Verification } from './types';
import { apiCall } from '@/util/api';
import { UserLogic } from '@/modules/user';
import type { User } from '@/modules/user';
import { SixDigitCode } from '@/models/six_digit_code';

/**
 * Initiate identity verification
 * @param level Verification level: "phone_verif" for participant-level or "email_verif"
 * @param email Email address
 * @param phone Phone number
 * @param continuePath Optional app path to redirect to after verification. If not provided, uses current window location.
 */
export async function postNewIdvVerification(
  level: typeof CLAIMS_LEVEL.PHONE | typeof CLAIMS_LEVEL.EMAIL,
  email: string,
  phone: string,
  continuePath?: string
): Promise<void> {
  // Determine continue path: use provided path, or default to current window location
  const finalContinuePath =
    continuePath ??
    (typeof window !== 'undefined' && window.location
      ? window.location.pathname
      : '/');

  const response = await apiCall('/api/idv/verifications', {
    method: 'POST',
    body: JSON.stringify({
      level,
      email: level === CLAIMS_LEVEL.EMAIL ? email : undefined,
      phone: level === CLAIMS_LEVEL.PHONE ? phone : undefined,
      continue: finalContinuePath,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to initiate identity verification');
  }

  // Token is stored automatically by apiCall via applyTokenFromResponse
  await response.json();
}

/**
 * Confirm identity verification using a verification ID and token or code
 * The backend sets the HTTP-only cookie with the new trust level
 * UserContext handles in-memory caching of user information
 * @param id Verification ID (UUID80 base64) from URL path
 * @param token Verification token from query parameter (optional if code is provided)
 * @param code Verification code (6 digits) (optional if token is provided)
 * @param previousPhone For a phone-number change, the number being moved away
 *   from. The backend texts it a change notice and never stores it; omit it when
 *   the number is unchanged or first-time.
 * @returns The verification (level + continuation path), the user, and the token
 */
export async function postIdvConfirmVerification(
  id: string,
  token?: string,
  code?: SixDigitCode,
  previousPhone?: string
): Promise<{
  verification?: Verification;
  user: User | null;
  token: string;
}> {
  // Build request body with either token or code
  const body: { token?: string; code?: string; previousPhone?: string } = {};
  if (code) {
    body.code = code.toString();
  } else if (token) {
    body.token = token;
  } else {
    throw new Error('Either token or code must be provided');
  }
  if (previousPhone) {
    body.previousPhone = previousPhone;
  }

  const response = await apiCall(
    `/api/idv/verifications/${encodeURIComponent(id)}/confirm`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to confirm identity verification');
  }

  const data = await response.json();

  // Parse user from JSON response
  const user = UserLogic.fromNestedUserJSON(data);

  // Token is stored automatically by apiCall via applyTokenFromResponse

  return {
    verification: data.verification as Verification | undefined,
    user,
    token: data.token,
  };
}
